import React, { useState } from 'react';

const HotelOverview = ({ overview, hotelName }) => {
  const [showCompleteDetails, setShowCompleteDetails] = useState(false);
  
  if (!overview) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {hotelName || 'Hotel Overview'}
          </h2>
          <p className="text-gray-600">No overview information available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Hotel Details - Takes 3 columns */}
          <div className="lg:col-span-3">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2L3 7v11a2 2 0 002 2h4v-6h2v6h4a2 2 0 002-2V7l-7-5z" />
                </svg>
              </div>
              <div>
                {(overview.hotelName || hotelName) && (
                  <h2 className="text-3xl font-bold text-gray-900">
                    {overview.hotelName || hotelName}
                  </h2>
                )}
                {(overview.rating || overview.ratingWord) && (
                  <div className="flex items-center mt-1">
                    {overview.rating && (
                      <div className="flex items-center mr-4">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-4 h-4 ${
                              i < Math.floor(overview.rating) ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                        <span className="ml-2 text-sm font-semibold text-gray-700">
                          {overview.rating}
                        </span>
                      </div>
                    )}
                    {overview.ratingWord && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {overview.ratingWord}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {overview.address && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Location</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">{overview.address}</p>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2 flex items-center group">
                      <svg className="w-4 h-4 mr-1 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      View on Map
                    </button>
                  </div>
                </div>
              </div>
            )}

            {overview.description && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    About This Hotel
                  </h3>
                  <button
                    onClick={() => setShowCompleteDetails(!showCompleteDetails)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    {showCompleteDetails ? (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        Show Less
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        View Complete Details
                      </>
                    )}
                  </button>
                </div>
                
                <div 
                  className={`text-gray-700 leading-relaxed transition-all duration-500 ${
                    showCompleteDetails ? 'max-h-none' : 'line-clamp-3 max-h-20 overflow-hidden'
                  }`}
                  dangerouslySetInnerHTML={{ __html: overview.description }} 
                />
                
                {/* Complete Details Section - Only show if there's additional data */}
                {showCompleteDetails && (overview.additionalInfo || overview.policies) && (
                  <div className="mt-6 space-y-4 border-t pt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Complete Hotel Information</h4>
                    
                    {overview.additionalInfo && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(overview.additionalInfo).map(([category, info], index) => (
                          <div key={category} className="bg-blue-50 rounded-lg p-4">
                            <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center capitalize">
                              <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                              </svg>
                              {category.replace(/([A-Z])/g, ' $1')}
                            </h5>
                            <div className="space-y-2 text-sm">
                              {Array.isArray(info) ? (
                                info.map((item, idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span className="text-gray-600">{item.label}:</span>
                                    <span className="font-medium text-gray-900">{item.value}</span>
                                  </div>
                                ))
                              ) : typeof info === 'object' ? (
                                Object.entries(info).map(([key, value], idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span className="text-gray-600">{key}:</span>
                                    <span className="font-medium text-gray-900">{value}</span>
                                  </div>
                                ))
                              ) : (
                                <p className="text-gray-700">{info}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Policies Section - Only show if policies exist */}
                    {overview.policies && (
                      <div className="bg-gray-50 rounded-lg p-4 mt-4">
                        <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                          </svg>
                          Hotel Policies & Guidelines
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {Object.entries(overview.policies).map(([category, policyList], index) => (
                            <div key={category}>
                              <h6 className="font-medium text-gray-900 mb-2 capitalize">
                                {category.replace(/([A-Z])/g, ' $1')}:
                              </h6>
                              <ul className="space-y-1 text-gray-600">
                                {Array.isArray(policyList) ? (
                                  policyList.map((policy, idx) => (
                                    <li key={idx}>• {policy}</li>
                                  ))
                                ) : (
                                  <li>• {policyList}</li>
                                )}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Check-in/Check-out Times - Enhanced */}
            {(overview.checkInTime || overview.checkOutTime) && (
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Check-in & Check-out
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {overview.checkInTime && (
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">Check-in</p>
                        <p className="text-lg font-bold text-gray-900">{overview.checkInTime}</p>
                      </div>
                    </div>
                  )}
                  {overview.checkOutTime && (
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">Check-out</p>
                        <p className="text-lg font-bold text-gray-900">{overview.checkOutTime}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Price and Reviews Sidebar - Takes 1 column */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 sticky top-32 border border-blue-100">
              {/* Price Section - Only show if price exists */}
              {overview.price && (
                <div className="mb-6 text-center">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-white/50">
                    <div className="text-sm text-gray-500 mb-2 uppercase tracking-wide">Starting from</div>
                    <div className="flex items-baseline justify-center mb-2">
                      <span className="text-lg text-gray-500 mr-1">₹</span>
                      <span className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        {new Intl.NumberFormat('en-IN').format(overview.price)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">per night</p>
                    {overview.crossOutPrice && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-400 line-through">₹{overview.crossOutPrice}</span>
                        <span className="ml-2 text-green-600 font-semibold">
                          Save ₹{new Intl.NumberFormat('en-IN').format(parseFloat(overview.crossOutPrice.replace(/[^\d.-]/g, '')) - overview.price)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rating Section - Only show if rating or review exists */}
              {(overview.rating || overview.review) && (
                <div className="mb-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-white/50">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Guest Reviews
                    </h4>
                    
                    {overview.rating && (
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-4 h-4 ${
                                i < Math.floor(overview.rating) ? 'text-yellow-400' : 'text-gray-300'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                          <span className="ml-2 text-lg font-bold text-gray-900">
                            {overview.rating}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {overview.ratingWord && (
                      <div className="mb-3">
                        <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                          {overview.ratingWord}
                        </span>
                      </div>
                    )}
                    
                    {overview.review && (
                      <p className="text-sm text-gray-600">
                        Based on {overview.review} reviews
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Location Rating - Only show as a simple card if no other data */}
              {!overview.price && !overview.rating && !overview.review && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-white/50">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Hotel Information
                  </h4>
                  <p className="text-sm text-gray-600">
                    Detailed information will be displayed here when available.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelOverview;